import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

export type AppLang = 'fr_CA' | 'en_CA';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app.lang';
  private readonly supported: AppLang[] = ['fr_CA', 'en_CA'];
  private readonly defaultLang: AppLang = 'fr_CA';

  private lang$ = new BehaviorSubject<AppLang>(this.defaultLang);
  private renderer: Renderer2;
  public currentLang$ = this.lang$.asObservable();

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public init(): void {
    this.translate.addLangs(this.supported);
    this.translate.setDefaultLang(this.defaultLang);

    const stored = (localStorage.getItem(this.STORAGE_KEY) || '').toLowerCase();
    const browser = (navigator.language || '').toLowerCase();

    const resolved =
      this.normalizeLocale(stored) ||
      this.normalizeLocale(browser) ||
      this.defaultLang;

    this.apply(resolved);
  }

  public setLanguage(lang: AppLang) {
    if (!this.supported.includes(lang)) return;
    localStorage.setItem(this.STORAGE_KEY, lang);
    this.apply(lang);
  }

  public get currentLang(): AppLang {
    return this.lang$.value;
  }

  private normalizeLocale(input: string | null): AppLang | null {
    if (!input) return null;
    if (input.startsWith('fr')) return 'fr_CA';
    if (input.startsWith('en')) return 'en_CA';
    return null;
  }

  private apply(lang: AppLang) {
    this.translate.use(lang);
    this.lang$.next(lang);
    const htmlElement = this.document?.documentElement;
    if (!htmlElement) return;

    this.renderer.setAttribute(htmlElement, 'lang', lang.replace('_', '-'));
    this.renderer.setAttribute(htmlElement, 'dir', 'ltr');
  }
}
